import api, { ApiResponse } from './api';

export interface Tenant {
  id: number;
  name: string;
  building_id: number;
  location: string | null;
  mailroom_email: string | null;
  central_delivery_enabled: boolean;
  central_delivery_location: string | null;
  status: number;
  contacts_count?: number;
  building?: {
    id: number;
    name: string;
    full_address: string;
  };
  created_at: string;
  updated_at: string;
}

export interface TenantContact {
  id: number;
  tenant_id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  floor: string | null;
  status: number;
}

interface TenantListResponse {
  data: Tenant[];
  next_page: number;
}

interface TenantContactListResponse {
  data: TenantContact[];
  next_page: number;
}

export const tenantService = {
  async getTenants(buildingId: number): Promise<Tenant[]> {
    const response = await api.get<ApiResponse<TenantListResponse>>(
      `/manage-company/1/${buildingId}`
    );

    if (response.data.CODE === 200) {
      const result = response.data.RESULT;
      if (result && Array.isArray(result.data)) {
        return result.data;
      }
      return [];
    }

    if (response.data.CODE === 450) {
      return [];
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch tenants');
  },

  async getTenant(id: number): Promise<Tenant> {
    const response = await api.get<ApiResponse<Tenant>>(
      `/manage-company/show/${id}`
    );

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch tenant');
  },

  async updateTenant(id: number, data: Partial<Tenant>): Promise<Tenant> {
    const response = await api.put<ApiResponse<Tenant>>(
      `/manage-company/${id}`,
      data
    );

    if (response.data.CODE === 200) {
      return response.data.RESULT;
    }

    throw new Error(response.data.MESSAGE || 'Failed to update tenant');
  },

  async getTenantContacts(tenantId: number): Promise<TenantContact[]> {
    const response = await api.get<ApiResponse<TenantContactListResponse>>(
      `/manage-contact/${tenantId}/1`
    );

    if (response.data.CODE === 200) {
      const result = response.data.RESULT;
      if (result && Array.isArray(result.data)) {
        return result.data;
      }
      return [];
    }

    if (response.data.CODE === 450) {
      return [];
    }

    throw new Error(response.data.MESSAGE || 'Failed to fetch tenant contacts');
  },
};

export default tenantService;
